import { FlashPlayer } from "@/entities/game";
import { useApp } from "@/shared/hooks/useApps";
import { useAuth } from "@/shared/hooks/useAuth";
import type { Game } from "@/shared/types/flash";
import { GameLayout } from "@/widgets/layout";
import { AxiosError } from "axios";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./GamePlayPage.module.css";

export const GamePlayPage: React.FC = () => {
  const { gameSlug } = useParams<{ gameSlug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const {
    data: app,
    isLoading: isAppLoading,
    error: appError,
  } = useApp(gameSlug || "");

  const handleGoBack = () => {
    navigate("/games");
  };

  const game: Game | null = app
    ? {
        appId: app.id.toString(),
        title: app.title,
        type: app.type || (app.url ? "url" : "local"),
        fileName: app.fileName,
        url: app.url,
      }
    : null;

  if (isAuthLoading) {
    return (
      <GameLayout isLoading={true} isCompact={true}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingContent}>
            <div className={styles.loadingText}>Проверка авторизации...</div>
          </div>
        </div>
      </GameLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <GameLayout isCompact={true}>
        <div className={styles.container}>
          <div className={styles.errorCard}>
            <h1 className={styles.errorTitle}>Доступ ограничен</h1>
            <p className={styles.errorText}>
              Для доступа к играм необходимо войти в систему
            </p>
            <div className={styles.buttons}>
              <button
                onClick={() =>
                  navigate("/login", {
                    state: { from: { pathname: `/game/${gameSlug}` } },
                  })
                }
                className={`${styles.button} ${styles.buttonBlue}`}
              >
                Войти
              </button>
              <button
                onClick={handleGoBack}
                className={`${styles.button} ${styles.buttonGray}`}
              >
                Назад
              </button>
            </div>
          </div>
        </div>
      </GameLayout>
    );
  }

  if (!gameSlug) {
    return (
      <GameLayout isCompact={true}>
        <div className={styles.loadingContainer}>
          <div className={styles.errorCard}>
            <h1 className={styles.errorTitleRed}>Ошибка</h1>
            <p className={styles.errorText}>ID игры не указан</p>
            <button
              onClick={handleGoBack}
              className={`${styles.button} ${styles.buttonGray}`}
            >
              Назад
            </button>
          </div>
        </div>
      </GameLayout>
    );
  }

  if (isAppLoading) {
    return (
      <GameLayout isCompact={true}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingContent}>
            <div className={styles.loadingText}>Загрузка игры...</div>
          </div>
        </div>
      </GameLayout>
    );
  }

  if (appError) {
    const errorMessage = (() => {
      const axiosError = appError as AxiosError;
      if (axiosError?.response?.status === 404) {
        return `Игра с ID "${gameSlug}" не найдена`;
      }
      return "Ошибка при загрузке игры";
    })();

    return (
      <GameLayout>
        <div className={styles.container}>
          <div className={styles.errorCard}>
            <h1 className={styles.errorTitleRed}>Ошибка</h1>
            <p className={styles.errorText}>{errorMessage}</p>
            <div className={styles.buttons}>
              <button
                onClick={handleGoBack}
                className={`${styles.button} ${styles.buttonGray}`}
              >
                Назад
              </button>
              <button
                onClick={() => navigate("/games")}
                className={`${styles.button} ${styles.buttonBlue}`}
              >
                К списку игр
              </button>
            </div>
          </div>
        </div>
      </GameLayout>
    );
  }

  if (!app) {
    return (
      <GameLayout>
        <div className={styles.container}>
          <div className={styles.errorCard}>
            <h1 className={styles.errorTitleRed}>Игра не найдена</h1>
            <p className={styles.errorText}>
              Запрашиваемая игра не существует или была удалена
            </p>
          </div>
        </div>
      </GameLayout>
    );
  }

  if (!game) {
    return (
      <GameLayout isCompact={true}>
        <div className={styles.loadingContainer}>
          <div className={styles.errorCard}>
            <h1 className={styles.errorTitleRed}>Игра не найдена</h1>
            <p className={styles.errorText}>
              Запрашиваемая игра не существует или была удалена
            </p>
          </div>
        </div>
      </GameLayout>
    );
  }

  return (
    <GameLayout isCompact={true}>
      <div className={styles.content}>
        <FlashPlayer game={game} onExit={handleGoBack} />
      </div>
    </GameLayout>
  );
};
